'use client';

import { type ReactNode, type ReactElement, useContext, useEffect } from 'react';
import { StoreContext } from './store.provider';
import { useRouter, usePathname, useSearchParams } from 'next/navigation';
import { observer } from 'mobx-react-lite';
import { pathnames } from '@/lib/constants';
import { useAccount, useRefresh } from '@/lib/services/auth/auth.service';

export default observer(function AuthProvider({
  children,
}: Readonly<{ readonly children: ReactNode }>): ReactElement {
  const pathname = usePathname();
  const router = useRouter();
  const searchParams = useSearchParams();
  const { userStore } = useContext(StoreContext);
  const { account } = useAccount();
  const { refresh } = useRefresh();

  const redirect = (isAuth: boolean) => {
    const onAuth = searchParams.get('on-auth');
    if (isAuth && onAuth) return router.push(Buffer.from(onAuth, 'base64').toString('utf-8'));
    if (isAuth && pathnames.requiresNoAuth(pathname)) return router.push('/');
    else if (!isAuth && pathnames.requiresAuth(pathname))
      return router.push(`/auth?on-auth=${Buffer.from(pathname).toString('base64')}`);
  };

  useEffect(() => {
    (async () => {
      if (userStore.isAuth) return redirect(userStore.isAuth);
      if (localStorage.getItem('access-token') && !userStore.isAuth) {
        try {
          const isAuth = await refresh.mutateAsync();
          return redirect(isAuth.ok);
        } catch {
          localStorage.removeItem('access-token');
          return redirect(false);
        }
      }
      return redirect(false);
    })();
  }, [userStore.isAuth, pathname]);

  useEffect(() => {
    (async () => {
      if (userStore.isAuth) await account.refetch();
    })();
  }, [userStore.isAuth]);

  return <>{children}</>;
});
